import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    HostBinding,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
    ViewContainerRef,
    input,
} from '@angular/core';

import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';

import { PlayerStatsSubscriptionService } from '../../../event-subscription/player-stats-subscription-service';
import { ScoreboardSubscriptionService } from '../../../event-subscription/scoreboard-subscription.service';
import { MyBetsBet, MyBetsBetBuilderLeg, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { ScoreboardViewmodelFactory } from '../../../scoreboards/models/scoreboard-viewmodel.factory';
import { ScoreboardViewModel } from '../../../scoreboards/models/scoreboard.viewmodel';
import { MyBetsEventModel } from '../../models/my-bets-event.model';
import {
    BetMarketInfo,
    MarketStatInfo,
    PlayerInfo,
    PlayerStatInfo,
    VisualizationConstants,
    VisualizationType,
} from '../../models/my-bets-visualization-types';
import { MyBetsVisualizationService } from '../../services/my-bets-visualization.service';
import { MyBetsH2HVisualizationComponent } from './my-bets-h2h-visualization.component';
import { MyBetsTargetVisualizationComponent } from './my-bets-target-visualization.component';
import { MyBetsTextualVisualizationComponent } from './my-bets-textual-visualization.component';
import { VisualizationBaseComponent } from './visualization-base.component';

@Component({
    standalone: true,
    imports: [],
    selector: 'ms-my-bets-visualization',
    template: `<div #host></div>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ScoreboardViewmodelFactory, MyBetsVisualizationService],
})
export class MyBetsVisualizationComponent implements OnInit, OnDestroy {
    event = input.required<MyBetsEventModel>();
    bet = input.required<MyBetsBet>();
    myBetsListContext = input.required<MyBetsListContext>();
    visualizationType = input.required<VisualizationType>();
    cashedOut = input(false);
    myBetsBetBuilderLeg = input<MyBetsBetBuilderLeg>();

    @ViewChild('host', { static: true, read: ViewContainerRef }) visualizationHost: ViewContainerRef;

    @HostBinding('class') get className() {
        return 'my-bets-visualization-host';
    }

    private vm: ScoreboardViewModel;
    private scoreboardSubscription: EventSubscription<MyBetsEventModel>;
    private playerStatsSubscription: EventSubscription<MyBetsEventModel>;
    private visualType: Type<VisualizationBaseComponent> | undefined;
    private visualizationComponentRef: ComponentRef<VisualizationBaseComponent>;
    private betMarketInfo: BetMarketInfo | null = null;

    private visualizationComponentMap = new Map<VisualizationType, Type<VisualizationBaseComponent>>([
        [VisualizationType.TargetVisualization, MyBetsTargetVisualizationComponent],
        [VisualizationType.H2hVisualization, MyBetsH2HVisualizationComponent],
        [VisualizationType.TextualVisualization, MyBetsTextualVisualizationComponent],
    ]);

    constructor(
        private scoreboardSubscriptionService: ScoreboardSubscriptionService<MyBetsEventModel>,
        private playerStatsSubscriptionService: PlayerStatsSubscriptionService<MyBetsEventModel>,
        private viewModelFactory: ScoreboardViewmodelFactory,
        private changeDetector: ChangeDetectorRef,
        private myBetsVisualizationService: MyBetsVisualizationService,
    ) {}

    ngOnInit(): void {
        this.cleanContainer();
        this.betMarketInfo = this.getBetMarketInfo();

        if (this.canInitializeVisualization()) {
            this.initializeVisualizationContainer();
            this.subscribe();
        }
    }

    ngOnDestroy(): void {
        this.scoreboardSubscription?.unsubscribe();
        this.playerStatsSubscription?.unsubscribe();
    }

    private canInitializeVisualization(): boolean {
        if (!this.betMarketInfo || !this.event()?.scoreboard?.started) {
            return false;
        }

        this.visualType = this.visualizationComponentMap.get(this.visualizationType());

        return !!this.visualType;
    }

    private initializeVisualizationContainer(): void {
        if (this.visualType) {
            this.visualizationComponentRef = this.visualizationHost.createComponent(this.visualType);
            this.vm = this.viewModelFactory.createMyBetsScoreBoardViewModel(this.event());
            this.updateVisualizationComponentInputs();
        }
    }

    protected onScoreboardUpdatedInternal = (): void => {
        const newVisualType = this.getVisualizationComponent(this.visualizationType());
        if (this.visualType !== newVisualType) {
            this.cleanContainer();
            this.initializeVisualizationContainer();
        } else {
            this.vm.updateFromScoreboard(this.event().scoreboard);
            this.updateVisualizationComponentInputs();
        }
        this.changeDetector.markForCheck();
    };

    private updateVisualizationComponentInputs(): void {
        if (this.betMarketInfo) {
            const playerStatInfo = this.getPlayerStatInfo(this.betMarketInfo);
            this.setComponentInputs(this.betMarketInfo, playerStatInfo);
        }
    }

    private getBetMarketInfo(): BetMarketInfo | null {
        if (this.myBetsBetBuilderLeg()) {
            return this.myBetsVisualizationService.getBetMarketInfoFromLeg(this.myBetsBetBuilderLeg()!);
        }

        return this.myBetsVisualizationService.getBetMarketInfoFromBet(this.bet());
    }

    private getPlayerStatInfo(betMarketInfo: BetMarketInfo): PlayerStatInfo {
        const { marketStatsInfo, statsType } = this.myBetsVisualizationService.marketStatsDetails(this.event(), betMarketInfo.marketId);

        const currentStatValue = this.myBetsVisualizationService.getCurrentStatValue(this.event(), statsType, marketStatsInfo, betMarketInfo);

        const targetStatValue = this.getTargetStatValue(marketStatsInfo, betMarketInfo);

        return {
            player1CurrStat: currentStatValue.player1Stat,
            player2CurrStat: currentStatValue.player2Stat,
            targetStat: targetStatValue,
            optionName: betMarketInfo.optionName,
            player: this.getPlayerInfo(marketStatsInfo, betMarketInfo),
        };
    }

    private getTargetStatValue(marketStatsInfo: MarketStatInfo | undefined, betMarketInfo: BetMarketInfo): number[] {
        return (
            (marketStatsInfo?.templateId && this.myBetsVisualizationService.getTargetStatByTemplateId(marketStatsInfo.templateId)) ||
            this.myBetsVisualizationService.getTargetStatByName(betMarketInfo.optionName) ||
            this.myBetsVisualizationService.getTargetStatByName(betMarketInfo.marketName) ||
            []
        );
    }

    private setComponentInputs(betMarketInfo: BetMarketInfo, playerStatInfo: PlayerStatInfo): void {
        this.visualizationComponentRef.setInput('event', this.event());
        this.visualizationComponentRef.setInput('bet', this.bet());
        this.visualizationComponentRef.setInput('statsData', playerStatInfo);
        this.visualizationComponentRef.setInput('myBetsListContext', this.myBetsListContext());
        this.visualizationComponentRef.setInput('isCashedOut', this.cashedOut());
        this.visualizationComponentRef.setInput('betMarketInfo', betMarketInfo);
    }

    private getPlayerInfo(marketStatInfo: MarketStatInfo | undefined, betMarketInfo: BetMarketInfo): PlayerInfo {
        if (!marketStatInfo) {
            return { playerName: '', playerId: 0 };
        }

        const matchingOption = marketStatInfo.options.find((o) => o.id === betMarketInfo.optionId);

        if (matchingOption) {
            const index = marketStatInfo.options.indexOf(matchingOption);

            return {
                playerName: index % 2 === 0 ? VisualizationConstants.HomePlayer : VisualizationConstants.AwayPlayer,
                playerId: matchingOption.playerId ?? 0,
            };
        }

        return { playerName: '', playerId: 0 };
    }

    private getVisualizationComponent(visualizationType: VisualizationType): Type<VisualizationBaseComponent> | undefined {
        return this.visualizationComponentMap.get(visualizationType);
    }

    private cleanContainer(): void {
        this.visualizationHost.clear();
    }

    private subscribe(): void {
        if (this.event()) {
            this.scoreboardSubscription = this.scoreboardSubscriptionService.subscribe([
                { event: this.event(), callback: this.onScoreboardUpdatedInternal },
            ]);

            this.playerStatsSubscription = this.playerStatsSubscriptionService.subscribe([
                { event: this.event(), callback: this.onScoreboardUpdatedInternal },
            ]);
        }
    }
}
